import {
  BarChart3, BookOpenCheck, Building2, Download, GraduationCap, LayoutDashboard,
  LogOut, Menu, Search, ShieldCheck, Trophy, UserRound, Users, X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { adminApi, api, downloadAdminExport } from '../../api.js';

const menuItems = [
  { to: '/admin', end: true, label: '数据概览', icon: LayoutDashboard },
  { to: '/admin/participants', label: '参赛人员', icon: Users },
  { to: '/admin/rankings', label: '答题排名', icon: Trophy },
  { to: '/admin/colleges', label: '学院排名', icon: Building2 },
];

function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: 'admin', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await api('/api/admin/login', { method: 'POST', body: JSON.stringify(form) });
      sessionStorage.setItem('adminToken', data.token);
      onLogin(true);
      navigate('/admin');
    } catch (requestError) {
      setError(requestError.message);
    } finally { setLoading(false); }
  }

  return (
    <main className="admin-login-page">
      <section className="admin-login-brand">
        <div className="admin-brand-mark"><ShieldCheck size={34} /></div>
        <span>湖北经济学院</span>
        <h1>民族团结进步<br />知识竞赛管理平台</h1>
        <p>集中管理参赛信息、成绩排名与学院数据，支持检索和 Excel 导出。</p>
        <div className="login-brand-data"><div><b>104</b><span>题完整题库</span></div><div><b>2</b><span>次参赛机会</span></div><div><b>3</b><span>类数据报表</span></div></div>
      </section>
      <section className="admin-login-panel">
        <form onSubmit={submit}>
          <div className="login-mobile-brand"><ShieldCheck size={25} /><b>竞赛管理平台</b></div>
          <span className="admin-eyebrow">ADMIN CONSOLE</span>
          <h2>管理员登录</h2>
          <p>请输入管理账号进入数据后台</p>
          <label><span>管理账号</span><input value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} autoComplete="username" /></label>
          <label><span>登录密码</span><input type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} placeholder="请输入密码" autoComplete="current-password" /></label>
          {error && <div className="admin-login-error">{error}</div>}
          <button type="submit" disabled={loading}>{loading ? '正在验证…' : '登录管理后台'}</button>
          <a href="/">返回答题首页</a>
        </form>
      </section>
    </main>
  );
}

function AdminLayout({ onLogout }) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const current = menuItems.find((item) => item.end ? location.pathname === item.to : location.pathname.startsWith(item.to)) || menuItems[0];
  return (
    <div className="admin-shell">
      <aside className={menuOpen ? 'admin-sidebar open' : 'admin-sidebar'}>
        <div className="admin-logo"><span><BookOpenCheck size={24} /></span><div><b>知识竞赛</b><small>管理控制台</small></div><button onClick={() => setMenuOpen(false)}><X size={20} /></button></div>
        <nav>{menuItems.map(({ icon: Icon, ...item }) => <NavLink key={item.to} {...item} onClick={() => setMenuOpen(false)}><Icon size={19} />{item.label}</NavLink>)}</nav>
        <div className="admin-sidebar-note"><ShieldCheck size={18} /><div><b>数据安全提示</b><span>导出文件含个人信息，请妥善保管。</span></div></div>
        <button className="logout-button" onClick={onLogout}><LogOut size={18} />退出登录</button>
      </aside>
      {menuOpen && <button className="sidebar-mask" onClick={() => setMenuOpen(false)} aria-label="关闭菜单" />}
      <main className="admin-main">
        <header className="admin-topbar"><button className="menu-button" onClick={() => setMenuOpen(true)}><Menu size={21} /></button><div><span>民族团结进步知识竞赛</span><b>{current.label}</b></div><div className="admin-user"><span>管</span><div><b>管理员</b><small>admin</small></div></div></header>
        <div className="admin-content"><Routes><Route index element={<Dashboard />} /><Route path="participants" element={<Participants />} /><Route path="rankings" element={<Rankings />} /><Route path="colleges" element={<Colleges />} /><Route path="*" element={<Navigate to="/admin" replace />} /></Routes></div>
      </main>
    </div>
  );
}

function PageHeading({ eyebrow, title, description, exportType }) {
  const [exporting, setExporting] = useState(false);
  async function exportFile() {
    setExporting(true);
    try { await downloadAdminExport(exportType); } finally { setExporting(false); }
  }
  return <div className="admin-page-head"><div><span>{eyebrow}</span><h1>{title}</h1><p>{description}</p></div>{exportType && <button onClick={exportFile} disabled={exporting}><Download size={17} />{exporting ? '正在导出…' : '导出 Excel'}</button>}</div>;
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [collegeRows, setCollegeRows] = useState([]);
  useEffect(() => { Promise.all([adminApi('/api/admin/stats'), adminApi('/api/admin/college-rankings')]).then(([summary, colleges]) => { setStats(summary); setCollegeRows(colleges.rows.slice(0, 6)); }); }, []);
  if (!stats) return <AdminLoading />;
  const cards = [
    { label: '参赛总人数', value: stats.participantCount, hint: `学生 ${stats.studentCount} · 教职工 ${stats.staffCount}`, icon: Users, tone: 'red' },
    { label: '完成答题次数', value: stats.completedAttemptCount, hint: '每人最多 2 次', icon: BookOpenCheck, tone: 'blue' },
    { label: '平均成绩', value: `${stats.averageScore}`, suffix: '分', hint: '全部已完成答卷', icon: BarChart3, tone: 'orange' },
    { label: '题库总量', value: stats.questionBankTotal, hint: '84 单选 · 20 多选', icon: GraduationCap, tone: 'green' },
  ];
  return <><PageHeading eyebrow="OVERVIEW" title="数据概览" description="掌握竞赛参与情况、答题进度与学院表现。" /><div className="stat-grid">{cards.map(({ icon: Icon, ...card }) => <article className={`stat-card ${card.tone}`} key={card.label}><div className="stat-icon"><Icon size={22} /></div><span>{card.label}</span><div><strong>{card.value}</strong>{card.suffix && <small>{card.suffix}</small>}</div><p>{card.hint}</p></article>)}</div><div className="dashboard-grid"><section className="admin-panel"><div className="panel-head"><div><span>学院表现</span><h2>学院平均正确率 TOP 6</h2></div><NavLink to="/admin/colleges">查看全部</NavLink></div><div className="college-bars">{collegeRows.map((row) => <div key={row.college}><div><b>{row.rank}</b><span>{row.college}</span><strong>{row.averageAccuracy}%</strong></div><div className="bar-track"><i style={{ width: `${row.averageAccuracy}%` }} /></div></div>)}</div></section><section className="admin-panel quick-panel"><div className="panel-head"><div><span>快捷入口</span><h2>常用数据操作</h2></div></div><NavLink to="/admin/participants"><Users size={20} /><div><b>检索参赛人员</b><span>按姓名、学号、单位查询</span></div></NavLink><NavLink to="/admin/rankings"><Trophy size={20} /><div><b>查看答题排名</b><span>学生和教职工分组统计</span></div></NavLink><button onClick={() => downloadAdminExport('colleges')}><Download size={20} /><div><b>导出学院排名</b><span>生成 Excel 数据表</span></div></button></section></div></>;
}

function useTableData(endpoint, query, group = '') {
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => { setLoading(true); adminApi(`${endpoint}?q=${encodeURIComponent(query)}&group=${group}`).then(setData).finally(() => setLoading(false)); }, [endpoint, query, group]);
  useEffect(() => { const timer = setTimeout(load, 180); return () => clearTimeout(timer); }, [load]);
  return { ...data, loading, reload: load };
}

function TableTools({ query, setQuery, group, setGroup, resultCount, hideGroup = false }) {
  return <div className="table-tools"><div className="admin-search"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索姓名、编号、单位或班级" />{query && <button onClick={() => setQuery('')}><X size={15} /></button>}</div>{!hideGroup && <div className="filter-tabs"><button className={group === '' ? 'active' : ''} onClick={() => setGroup('')}>全部</button><button className={group === 'student' ? 'active' : ''} onClick={() => setGroup('student')}>学生</button><button className={group === 'staff' ? 'active' : ''} onClick={() => setGroup('staff')}>教职工</button></div>}<span className="result-count">共 {resultCount} 条</span></div>;
}

function Participants() {
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('');
  const data = useTableData('/api/admin/participants', query, group);
  return <><PageHeading eyebrow="PARTICIPANTS" title="参赛人员" description="查看并检索所有学生与教职工的参赛信息。" exportType="participants" /><section className="admin-table-panel"><TableTools {...{ query, setQuery, group, setGroup }} resultCount={data.total} /><ResponsiveTable loading={data.loading} columns={['姓名', '组别', '学号 / 工号', '学院 / 单位', '班级', '答题次数', '最高分', '报名时间']} rows={data.rows.map((row) => [<b>{row.name}</b>, <GroupBadge group={row.group} />, row.identifier, row.organization, row.className || '-', `${row.attemptCount} / 2`, row.bestScore == null ? '-' : <strong className="score-text">{row.bestScore}</strong>, formatDate(row.createdAt)])} /></section></>;
}

function Rankings() {
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('student');
  const data = useTableData('/api/admin/rankings', query, group);
  return <><PageHeading eyebrow="RANKINGS" title="答题排名" description="按最高分排序，同分时用时更短者优先。学生与教职工分开排名。" exportType="rankings" /><section className="admin-table-panel"><TableTools {...{ query, setQuery, group, setGroup }} resultCount={data.total} /><ResponsiveTable loading={data.loading} columns={['排名', '姓名', '组别', '学号 / 工号', '学院 / 单位', '班级', '最高分', '最佳用时', '完成时间']} rows={data.rows.map((row) => [<RankBadge rank={row.rank} />, <b>{row.name}</b>, <GroupBadge group={row.group} />, row.identifier, row.organization, row.className || '-', <strong className="score-text">{row.score}</strong>, `${(row.durationMs / 1000).toFixed(1)} 秒`, formatDate(row.completedAt)])} /></section></>;
}

function Colleges() {
  const [query, setQuery] = useState('');
  const data = useTableData('/api/admin/college-rankings', query);
  return <><PageHeading eyebrow="COLLEGE RANKING" title="学院排名" description="按各学院参赛学生最佳成绩的平均正确率计算。" exportType="colleges" /><section className="admin-table-panel"><TableTools query={query} setQuery={setQuery} resultCount={data.total} hideGroup /><ResponsiveTable loading={data.loading} columns={['排名', '学院', '有效参赛人数', '平均正确率', '满分人数', '表现']} rows={data.rows.map((row) => [<RankBadge rank={row.rank} />, <b>{row.college}</b>, `${row.participantCount} 人`, <strong className="score-text">{row.averageAccuracy}%</strong>, `${row.fullScoreCount} 人`, <div className="mini-progress"><i style={{ width: `${row.averageAccuracy}%` }} /></div>])} /></section></>;
}

function ResponsiveTable({ columns, rows, loading }) {
  if (loading) return <AdminLoading compact />;
  if (rows.length === 0) return <div className="admin-empty"><Search size={28} /><b>没有找到符合条件的数据</b><span>修改搜索词或筛选条件后再试。</span></div>;
  return <div className="table-scroll"><table><thead><tr>{columns.map((column) => <th key={column}>{column}</th>)}</tr></thead><tbody>{rows.map((row, index) => <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex} data-label={columns[cellIndex]}>{cell}</td>)}</tr>)}</tbody></table></div>;
}

function GroupBadge({ group }) { return <span className={`group-badge ${group}`}>{group === 'student' ? '学生' : '教职工'}</span>; }
function RankBadge({ rank }) { return <span className={`admin-rank rank-${rank}`}>{rank}</span>; }
function AdminLoading({ compact = false }) { return <div className={compact ? 'admin-loading compact' : 'admin-loading'}><span /><p>正在载入数据…</p></div>; }
function formatDate(value) { return value ? new Intl.DateTimeFormat('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(value)) : '-'; }

export default function AdminApp() {
  const [authenticated, setAuthenticated] = useState(Boolean(sessionStorage.getItem('adminToken')));
  const location = useLocation();
  const isLogin = location.pathname === '/admin/login';
  const logout = useCallback(() => { sessionStorage.removeItem('adminToken'); setAuthenticated(false); }, []);
  if (!authenticated && !isLogin) return <Navigate to="/admin/login" replace />;
  if (authenticated && isLogin) return <Navigate to="/admin" replace />;
  if (isLogin) return <LoginPage onLogin={setAuthenticated} />;
  return <AdminLayout onLogout={logout} />;
}
